// Seção "Desembolso deste mês" do offcanvas do item (spec 8.4): de quais meses de
// execução veio o valor deste mês, por qual regra, quais ajustes tocam o mês, o
// comprometido × restante e a ação "Mover para outro mês".
//
// Dinheiro e competência saem de `./format` de propósito — `money` embrulha
// `formatNumberToCurrency`, que é um hook disfarçado (lê o contexto do projeto):
// só pode ser chamado no render de um componente, nunca em callback/laço fora do
// render.
import { useEffect, useState } from "react";

import { fetchDisbursementMonth, updateDisbursementAdjustment } from "./disbursementApi";
import { disbursementErrorDetail } from "./errorMessages";
import { competenceText, money } from "./format";

const COMPETENCE = /^\d{4}-(0[1-9]|1[0-2])$/;
const TABLE_MONTH = /^(0[1-9]|1[0-2])\/\d{4}$/;
// Reais em PT-BR: milhar por ponto (opcional) e decimais por vírgula.
const AMOUNT_IN_REAIS = /^(\d{1,3}(\.\d{3})+|\d+)(,\d+)?$/;

const MOVE_ERROR_TEXT = "Não foi possível mover o valor. Tente novamente.";
const MOVE_ERROR_PREFIX = "Não foi possível mover o valor.";


/**
 * `invalid_adjustment` é o único código cuja frase o próprio back-end escreve
 * (`message`) — o servidor sabe exatamente qual regra de negócio barrou o ajuste
 * (ex.: janela fechada, valor maior que o disponível), e o FE não tenta adivinhar.
 * Sem `message` cai no genérico igual aos demais códigos conhecidos/desconhecidos.
 */
const moveErrorText = (result) => {
  if (result?.errorCode === "invalid_adjustment" && result?.message) {
    return `${MOVE_ERROR_PREFIX} ${result.message}`;
  }
  // Mapa compartilhado com os dois editores (`errorMessages.js`).
  const detail = disbursementErrorDetail(result?.errorCode);
  return detail ? `${MOVE_ERROR_PREFIX} ${detail}` : MOVE_ERROR_TEXT;
};

/**
 * Folhas do dinheiro. `money()` chama `formatNumberToCurrency`, que lê
 * `useProjectContext` — é um hook disfarçado. Dentro de um `.map` a contagem de hooks
 * do componente hospedeiro passaria a depender do TAMANHO da lista, e dentro de um
 * galho condicional, do dado: as duas coisas disparam o "change in the order of
 * Hooks" do React. Isoladas aqui, cada instância chama o hook exatamente uma vez.
 *
 * Mesmo dentro de uma folha as chamadas de `money()` ficam no TOPO do corpo, nunca
 * atrás de um `?:` — é a mesma armadilha em escala menor.
 */
const ContributionRow = ({ contribution, canEditRule, onEditRule }) => (
  <li>
    <span className="source">{`de ${competenceText(contribution.sourceCompetence)}`}</span>
    <span className="rule">{contribution.ruleLabel}</span>
    {/* O reset global de `span` (`_fonts.scss`) zera o span interno: a classe
        do valor fica no de fora pra a formatação monetária não sumir. */}
    <span className="value"><span>{money(contribution.cents)}</span></span>
    {canEditRule ? (
      <button
        type="button"
        className="button button-secondary small"
        onClick={() => onEditRule(contribution.ruleKey)}
      >
        Ajustar regra
      </button>
    ) : null}
  </li>
);

const AdjustmentRow = ({ adjustment }) => {
  const movedText = money(adjustment.amountCents);
  const exceededText = money(adjustment.exceedsCents);
  return (
    <li>
      <span className="badge">ajuste</span>
      <span>
        {`Mover ${movedText} de ${competenceText(adjustment.fromCompetence)} para ${competenceText(adjustment.toCompetence)}`}
      </span>
      {adjustment.exceedsCents > 0 ? (
        <span className="warning">
          {`${exceededText} não couberam no mês de origem e continuam onde estavam.`}
        </span>
      ) : null}
    </li>
  );
};

const MonthTotals = ({ paidCents, toPayCents, remainingCents }) => (
  <div className="ampli-month-disbursement__totals">
    <div>
      <span>Pago</span>
      <strong>{money(paidCents)}</strong>
    </div>
    <div>
      <span>A pagar</span>
      <strong>{money(toPayCents)}</strong>
    </div>
    <div>
      <span>Comprometido</span>
      <strong>{money(paidCents + toPayCents)}</strong>
    </div>
    <div>
      <span>Previsto restante</span>
      <strong>{money(remainingCents)}</strong>
    </div>
  </div>
);

const MoveSentence = ({ amountCents, competence, toCompetence }) => (
  <p className="ampli-month-disbursement__sentence">
    {`Mover ${money(Number.isSafeInteger(amountCents) ? amountCents : 0)} de ${competenceText(competence)} para ${competenceText(toCompetence)}`}
  </p>
);

const FLAG_BADGES = Object.freeze({
  exceeds_origin: "excedente",
  over_planned: "acima do previsto",
});

const FLAG_LABELS = Object.freeze({
  exceeds_origin: "Um ajuste não coube inteiro no mês de origem.",
  over_planned: "O comprometido já passou do previsto deste item.",
});

/**
 * `tableMonthSelected` da tabela de projeção vem como `"MM/AAAA"`; a rota do mês
 * fala `"AAAA-MM"`. Helper puro e exportado porque o `ItemDetailOffcanvas` faz essa
 * conversão e ela precisa ser testável sem montar o offcanvas inteiro. Entrada
 * ausente ou malformada devolve `null` — a seção então não renderiza nem busca, em
 * vez de pedir uma competência inventada ao back-end.
 */
export const tableMonthToCompetence = (tableMonth) => {
  const text = String(tableMonth ?? "");
  if (!TABLE_MONTH.test(text)) return null;
  return `${text.slice(3)}-${text.slice(0, 2)}`;
};

/**
 * `"1.500,00"` → `150000`. Aritmética só com inteiros: multiplicar reais por 100 em
 * float erra casos como `8,285` (daria 828 em vez de 829). A terceira casa decimal
 * arredonda meio pra cima; qualquer coisa que não resulte em centavos inteiros
 * positivos devolve `NaN` (vazio, zero, negativo ou lixo) — o botão de confirmar lê
 * isso pra ficar travado.
 */
export const parseCentsFromReais = (input) => {
  const text = String(input ?? "").trim();
  if (!AMOUNT_IN_REAIS.test(text)) return NaN;
  const [whole, fraction = ""] = text.replace(/\./g, "").split(",");
  const padded = `${fraction}000`.slice(0, 3);
  const cents = Number(whole) * 100
    + Number(padded.slice(0, 2))
    + (Number(padded.slice(2, 3)) >= 5 ? 1 : 0);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : NaN;
};

const AmpliMonthDisbursementSection = ({
  item,
  competence,
  projectId,
  readOnly = false,
  onAdjusted,
  onEditRule,
}) => {
  const wbsCodeId = item?.wbs_code_id;
  const inScope = Boolean(projectId) && Boolean(wbsCodeId) && COMPETENCE.test(String(competence ?? ""));

  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [moving, setMoving] = useState(false);
  const [amount, setAmount] = useState("");
  const [toCompetence, setToCompetence] = useState("");
  const [saving, setSaving] = useState(false);
  const [moveError, setMoveError] = useState(null);
  // Incrementado a cada 200/202 de um "Mover": entra nas deps do efeito de busca
  // pra reler o mês do zero depois de um ajuste bem-sucedido, sem duplicar a
  // lógica de fetch nem depender do `onAdjusted` do chamador (que recarrega a
  // obra inteira, não necessariamente este mês).
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!inScope) return undefined;
    // `cancelled` guarda o `setState` depois do unmount (offcanvas fecha no meio da
    // leitura) e também descarta a resposta de uma competência/item já trocados.
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    (async () => {
      const payload = await fetchDisbursementMonth(projectId, competence, { wbs: wbsCodeId });
      if (cancelled) return;
      setMonth(payload ?? null);
      setLoadFailed(!payload);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [inScope, projectId, competence, wbsCodeId, reloadToken]);

  if (!inScope) return null;

  const amountCents = parseCentsFromReais(amount);
  const canConfirm = Number.isSafeInteger(amountCents)
    && COMPETENCE.test(toCompetence)
    && toCompetence !== competence
    && !saving;

  const closeMoveForm = () => {
    setMoving(false);
    setAmount("");
    setToCompetence("");
    setMoveError(null);
  };

  const confirmMove = async () => {
    // Recheque do somente-leitura na hora da ESCRITA: o formulário só aparece com
    // `readOnly === false`, mas a prop pode virar `true` (troca de obra, obra de
    // apresentação carregada depois) entre abrir o formulário e clicar em confirmar.
    if (!canConfirm || readOnly) return;
    setSaving(true);
    setMoveError(null);
    try {
      const result = await updateDisbursementAdjustment(projectId, {
        operation: "create",
        wbsCodeId,
        fromCompetence: competence,
        toCompetence,
        amountCents,
        description: "",
      });
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setMoveError(moveErrorText(result));
        return;
      }
      closeMoveForm();
      setReloadToken((token) => token + 1);
      await onAdjusted?.(result);
    } catch {
      // `onAdjusted` do chamador pode rejeitar (recarrega a obra): virar mensagem na
      // tela em vez de rejeição não tratada, e o botão volta a aceitar clique.
      setMoveError(MOVE_ERROR_TEXT);
    } finally {
      setSaving(false);
    }
  };

  const contributions = Array.isArray(month?.contributions) ? month.contributions : [];
  const adjustments = Array.isArray(month?.adjustments) ? month.adjustments : [];
  // `flags` é `{ exceeds_origin, over_planned }` no contrato real do BE
  // (`buildMonthProvenance`); a forma de array legada ainda é tolerada (cada
  // elemento vira uma flag `true`) pra não quebrar um payload antigo em cache.
  const flagsShape = month?.flags;
  const flagKeys = Array.isArray(flagsShape)
    ? flagsShape
    : Object.keys(FLAG_BADGES).filter((key) => Boolean(flagsShape?.[key]));
  const paidCents = Number.isFinite(month?.committedCents?.paid) ? month.committedCents.paid : 0;
  const toPayCents = Number.isFinite(month?.committedCents?.toPay) ? month.committedCents.toPay : 0;
  const isEmpty = Boolean(month) && contributions.length === 0 && adjustments.length === 0;

  return (
    <section className="ampli-month-disbursement">
      <h2>Desembolso deste mês</h2>

      {loading ? <p className="card-label">Carregando…</p> : null}
      {loadFailed && !loading ? (
        <p className="empty-state" role="alert">
          Não foi possível carregar a origem deste mês. Tente novamente.
        </p>
      ) : null}
      {isEmpty && !loading ? (
        <p className="empty-state">Nenhum desembolso previsto para este item neste mês.</p>
      ) : null}

      {contributions.length > 0 ? (
        <ul className="ampli-month-disbursement__contributions">
          {contributions.map((contribution) => (
            <ContributionRow
              key={`${contribution.ruleKey ?? "at_sight"}:${contribution.sourceCompetence}`}
              contribution={contribution}
              canEditRule={Boolean(
                contribution.ruleKey
                && !contribution.ruleKey.startsWith("adjustment:")
                && onEditRule
                && !readOnly,
              )}
              onEditRule={onEditRule}
            />
          ))}
        </ul>
      ) : null}

      {adjustments.length > 0 ? (
        <ul className="ampli-month-disbursement__adjustments">
          {adjustments.map((adjustment) => (
            <AdjustmentRow key={adjustment.entryId} adjustment={adjustment} />
          ))}
        </ul>
      ) : null}

      {month ? (
        <MonthTotals
          paidCents={paidCents}
          toPayCents={toPayCents}
          remainingCents={month.remainingCents}
        />
      ) : null}

      {flagKeys.length > 0 ? (
        <ul className="ampli-month-disbursement__flags">
          {flagKeys.map((flag) => (
            <li key={flag}>
              {/* `aria-label` carrega a frase inteira: a etiqueta curta sozinha não
                  explica nada para quem ouve a tela. */}
              <span className="badge badge-warning" aria-label={FLAG_LABELS[flag] ?? flag}>
                {FLAG_BADGES[flag] ?? flag}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {!readOnly && month ? (
        <div className="ampli-month-disbursement__move">
          {!moving ? (
            <button
              type="button"
              className="button button-secondary small"
              onClick={() => setMoving(true)}
            >
              Mover para outro mês
            </button>
          ) : (
            <>
              <label>
                <span>Valor a mover</span>
                <input
                  type="text"
                  aria-label="Valor a mover"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0,00"
                />
              </label>
              <label>
                <span>Mês de destino</span>
                <input
                  type="month"
                  aria-label="Mês de destino"
                  value={toCompetence}
                  onChange={(event) => setToCompetence(event.target.value)}
                />
              </label>
              <MoveSentence
                amountCents={amountCents}
                competence={competence}
                toCompetence={toCompetence}
              />
              {moveError ? <p className="error-message" role="alert">{moveError}</p> : null}
              <div className="offcanvas-buttons">
                <button
                  type="button"
                  className="button button-secondary small"
                  onClick={closeMoveForm}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="button button-accent small"
                  disabled={!canConfirm}
                  onClick={confirmMove}
                >
                  {saving ? "Movendo…" : "Confirmar"}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
};

export default AmpliMonthDisbursementSection;
